import type { ResultOf } from '@graphql-typed-document-node/core'
import {
  CouponOutlineIcon,
  HeartOutlineIcon,
  HistoryOutlineIcon,
  ListOutlineIcon,
  PersonOutlineIcon,
  TimesOutlineIcon,
  VacantOutlineIcon,
} from '@ikyucom/design-system-icons-react'
import React, { useCallback, useEffect, useRef } from 'react'
import type { FC, ReactElement } from 'react'
import { twMerge } from 'tailwind-merge'
import Image from '~/components/image/Image'
import { useEnv } from '~/env'
import { ME_FAVORITES_PATH } from '~/features/favorite/list/url'
import { useViewer } from '~/features/viewer/query'
import type { ViewerQuery } from '~/features/viewer/query'
import {
  DESIGN_SYSTEM_ASSETS_ORIGIN,
  LOGIN_SITE,
  MEMBER_RANK_LABEL,
  PAYPAY_IMAGE_URL,
  POINT_RATE_BY_RANK,
} from '~/lib/constants/constants'
import { SystemError } from '~/lib/error/systemError'
import { callLogout } from '~/lib/member/member'
import { money } from '~/lib/money/money'
import {
  getHelpUrl,
  getLoginUrl,
  getMemberRegistrationUrl,
  getMyPageUrl,
  getReservationConfirmUrl,
} from '~/lib/url'
import { useRedirectBackURL } from '~/navigation'
import { css, sva } from '~/styled-system/css'
import { hstack, vstack } from '~/styled-system/patterns'

type ListMenuProps = {
  children: ReactElement | ReactElement[]
  href: string
}
const ListMenu: React.FC<ListMenuProps> = ({ children, href }) => {
  return (
    <li className={css({ w: 'full' })}>
      <a
        className={css({
          display: 'flex',
          alignItems: 'center',
          columnGap: '2',
          fontSize: 'xl',
          fontWeight: 'bold',
        })}
        href={href}
      >
        {children}
      </a>
    </li>
  )
}

type Props = {
  close: () => void
  show: boolean
}

const styles = sva({
  slots: [
    'overlay',
    'container',
    'close',
    'menu',
    'heading',
    'rank',
    'vic',
    'point',
    'login',
    'menuList',
    'icon',
    'menuLink',
    'linkList',
  ],
  base: {
    overlay: {
      position: 'fixed',
      left: '0',
      top: '0',
      zIndex: '20',
      height: 'full',
      width: 'full',
      transition: '[all 300ms ease]',
    },
    close: {
      position: 'fixed',
      right: '[75%]',
      top: '0',
      width: '[40px]',
      height: '[40px]',
      color: 'white',
    },
    container: {
      position: 'fixed',
      right: '0',
      top: '0',
      zIndex: '50',
      height: 'full',
      width: '75%',
      overflowY: 'scroll',
      backgroundColor: 'white',
      transition: '[all 300ms ease]',
    },
    menu: {
      position: 'relative',
      zIndex: '50',
      height: 'full',
      width: 'full',
      backgroundColor: 'white',
      py: '8',
    },
    heading: {
      position: 'absolute',
      right: '0',
      top: '0',
      zIndex: '-10',
      height: 'auto',
      width: 'full',
    },
    rank: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '[76px]',
      height: '[18px]',
      fontSize: 'xs',
      color: 'white',
      lineHeight: 'none',
      borderRadius: 'sm',
      cursor: 'pointer',
      '&[data-rank=D]': { bgGradient: 'ikyu.diamond' },
      '&[data-rank=G]': { bgGradient: 'ikyu.gold' },
      '&[data-rank=P]': { bgGradient: 'ikyu.platinum' },
      '&[data-rank=R]': { bgGradient: 'ikyu.regular' },
    },
    vic: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '[76px]',
      height: '[18px]',
      fontFamily: 'serif',
      fontWeight: 'bold',
      fontSize: 'xs',
      color: 'white',
      bgGradient: 'ikyu.vic',
      borderRadius: 'sm',
    },
    point: {
      fontSize: 'xl',
      fontWeight: 'bold',
    },
    login: {
      display: 'flex',
      flexDirection: 'column',
      rowGap: '5',
      fontSize: 'xl',
      fontWeight: 'bold',
      color: 'tertiary',
    },
    menuList: vstack.raw({
      gap: '6',
      width: 'full',
      alignItems: 'start',
    }),
    icon: {
      color: 'primary',
    },
    menuLink: {
      display: 'block',
    },
    linkList: vstack.raw({
      gap: '5',
      borderTop: 'light',
      paddingTop: '8',
      alignItems: 'start',
      _last: {
        pb: '8',
      },
    }),
  },
})

const SideMenu: React.FC<Props> = ({ close, show }) => {
  const {
    data: viewerData,
    error: viewerError,
    isLoading: viewerIsLoading,
    refetch: refetchViewer,
  } = useViewer()
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (show && ref.current) {
      ref.current.scroll({ top: 0 })
    }
  }, [show])

  if (show && viewerError) {
    throw new SystemError(`GetViewerInfo ${viewerError.message ?? ''}`)
  }

  if (show && viewerIsLoading) {
    return null
  }

  const viewer = viewerData ?? { __typename: 'Guest' }
  const classes = styles()
  return (
    <div
      className={twMerge(
        classes.overlay,
        show
          ? css({ visibility: 'visible', background: 'black/60' })
          : css({ visibility: 'hidden' })
      )}
      onClick={close}
    >
      <TimesOutlineIcon className={classes.close} onClick={close} aria-label="閉じる" />
      <div
        className={twMerge(
          classes.container,
          show ? css({ transform: 'translateX(0)' }) : css({ transform: 'translateX(100%)' })
        )}
        // ハンバーガーメニュー内をクリックしてもモーダルを閉じさせない
        onClick={(e) => e.stopPropagation()}
      >
        <div className={classes.menu} ref={ref}>
          <SideMenuContent viewer={viewer} refetchViewer={refetchViewer} />
        </div>
      </div>
    </div>
  )
}

type SideMenuContentProps = {
  viewer: ResultOf<typeof ViewerQuery>['viewer']
  refetchViewer: () => Promise<{}>
}

const SideMenuContent: FC<SideMenuContentProps> =
  import.meta.env.VITE_MODE === 'ppg' ? PpgSideMenuContent : IkyuSideMenuContent

function IkyuSideMenuContent({ viewer, refetchViewer }: SideMenuContentProps) {
  const env = useEnv()
  const redirectBackUrl = useRedirectBackURL()

  const handleLogout = useCallback(async () => {
    await callLogout(env.MEMBER_ORIGIN)
    await refetchViewer()
  }, [env.MEMBER_ORIGIN, refetchViewer])

  const rank =
    viewer.__typename === 'Member' ? (viewer.rank as keyof typeof MEMBER_RANK_LABEL) : undefined
  const [rankText, pointRate] = rank
    ? [MEMBER_RANK_LABEL[rank], POINT_RATE_BY_RANK[rank]]
    : [undefined, undefined]

  const classes = styles()
  return (
    <>
      <div className={classes.heading}>
        {viewer.__typename === 'Member' ? (
          <Image
            src="https://restaurant.img-ikyu.com/rsImg/common/bg_member.jpeg"
            alt=""
            width={595}
            height={360}
            className={css({ width: 'full', objectFit: 'cover' })}
          />
        ) : (
          <Image
            src="https://restaurant.img-ikyu.com/rsImg/common/bg_guest.jpeg"
            alt=""
            width={595}
            height={360}
            className={css({ width: 'full', objectFit: 'cover' })}
          />
        )}
      </div>

      <div className={css({ spaceY: '10', px: '5' })}>
        {viewer.__typename === 'Member' ? (
          <div className={css({ spaceY: '2' })}>
            <p className={css({ fontSize: 'lg' })}>
              {/* viewer.nameが空文字のときは「未設定さん」と表示する */}
              <span className={css({ fontSize: '3xl', fontWeight: 'bold' })}>
                {viewer.name || '未設定'}
              </span>
              さん
            </p>
            {viewer.loginSite === LOGIN_SITE.Yahoo && (
              <p className={css({ spaceY: '1' })}>
                <p className={css({ fontSize: 'xs', color: 'gray.600' })}>
                  Yahoo! JAPAN IDでログインしています
                </p>
                <div className={hstack({ gap: '1' })}>
                  <p className={css({ fontSize: 'sm' })}>あなたの獲得率</p>
                  <Image src={PAYPAY_IMAGE_URL} width={14} height={14} alt="paypay" />
                  <span
                    className={css({
                      fontSize: 'sm',
                      fontWeight: 'bold',
                      color: 'accent.pink',
                    })}
                  >{`${pointRate}%`}</span>
                </div>
              </p>
            )}
            {viewer.pointVisible && (
              <p>
                <a href="https://my.ikyu.com/membership/" className={classes.rank} data-rank={rank}>
                  {rankText}
                </a>
              </p>
            )}
            {viewer.isVicSalonMember && (
              <a href={`${env.STAY_IKYU_ORIGIN}/vic-salon/`} className={classes.vic}>
                VIC Salon
              </a>
            )}
            {viewer.pointVisible && viewer.loginSite !== LOGIN_SITE.Yahoo && (
              <p>
                <a
                  href="https://my.ikyu.com/member/point/history/"
                  className={css({
                    fontSize: 'sm',
                    color: 'tertiary',
                    display: 'inline-block',
                  })}
                >
                  <span className={classes.point}>{money(viewer.point)}</span> ポイント
                </a>
              </p>
            )}
          </div>
        ) : (
          <div className={classes.login}>
            <a href={getLoginUrl(env, redirectBackUrl)}>ログイン</a>
            <a href={getMemberRegistrationUrl(env, redirectBackUrl)}>会員登録</a>
          </div>
        )}
        <div className={vstack({ gap: '6', alignItems: 'start' })}>
          <ul className={classes.menuList}>
            {viewer.__typename === 'Member' && (
              <ListMenu href={getMyPageUrl(env, redirectBackUrl)}>
                <PersonOutlineIcon width={20} height={20} className={classes.icon} />
                <span>マイページ</span>
              </ListMenu>
            )}
            <ListMenu href={getReservationConfirmUrl(env)}>
              <ListOutlineIcon width={20} height={20} className={classes.icon} />
              <span>予約確認・変更</span>
            </ListMenu>
            <ListMenu href="/rsSd/main/script/viewHistory.asp">
              <HistoryOutlineIcon width={20} height={20} className={classes.icon} />
              <span>最近見たレストラン</span>
            </ListMenu>
            <ListMenu href={ME_FAVORITES_PATH}>
              <HeartOutlineIcon width={20} height={20} className={classes.icon} />
              <span>お気に入り</span>
            </ListMenu>
            <ListMenu href="/rsSd/main/script/vacancyNoticeList.asp">
              <VacantOutlineIcon width={20} height={20} className={classes.icon} />
              <span>空席通知</span>
            </ListMenu>
            {viewer.__typename === 'Member' && (
              <ListMenu href={`${env.MEMBER_ORIGIN}/member/coupon/?service=RS`}>
                <CouponOutlineIcon width={20} height={20} className={classes.icon} />
                <span>獲得済みクーポン</span>
              </ListMenu>
            )}
          </ul>

          <ul className={classes.menuList}>
            {viewer.__typename === 'Member' && (
              <li>
                <a href={`${env.MEMBER_ORIGIN}/member/tieupsite/`} className={classes.menuLink}>
                  ID連携（LINE、Googleなど）
                </a>
              </li>
            )}
            <li>
              <a
                href="https://www.ikyu.com/sd/help/faq/start.aspx"
                className={classes.menuLink}
                target="_blank"
                rel="noreferrer"
              >
                ヘルプ・お問い合わせ
              </a>
            </li>
            {viewer.__typename === 'Member' && (
              <li onClick={handleLogout} className={classes.menuLink}>
                ログアウト
              </li>
            )}
          </ul>
        </div>
        <ul className={classes.linkList}>
          <li>
            <a href="https://www.ikyu.co.jp/policy/" className={classes.menuLink}>
              個人情報保護方針
            </a>
          </li>
          <li>
            <a href={`${env.MEMBER_ORIGIN}/terms`} className={classes.menuLink}>
              規約集
            </a>
          </li>
          <li>
            <a href="https://www.ikyu.co.jp/" className={classes.menuLink}>
              企業情報
            </a>
          </li>
        </ul>
        <ul className={classes.linkList}>
          <li>
            <a href="https://www.ikyu.com/sd/" className={classes.menuLink}>
              国内宿泊予約
            </a>
          </li>
          <li>
            <a href="https://spa.ikyu.com" className={classes.menuLink}>
              スパ予約
            </a>
          </li>
          <li>
            <a href="https://www.ikyu.com/global/" className={classes.menuLink}>
              海外宿泊予約
            </a>
          </li>
          <li>
            <a href="https://otoriyose.ikyu.com/" className={classes.menuLink}>
              お取り寄せ
            </a>
          </li>
          <li>
            <a href="https://furusato.ikyu.com/" className={classes.menuLink}>
              ふるさと納税
            </a>
          </li>
        </ul>
      </div>
    </>
  )
}

function PpgSideMenuContent({ viewer }: SideMenuContentProps) {
  const env = useEnv()
  const redirectBackUrl = useRedirectBackURL()
  const classes = styles()
  return (
    <>
      <div className={classes.heading}>
        <Image
          src="https://restaurant.img-ikyu.com/paypaygourmet/img/bg_menu.png"
          alt=""
          width={595}
          height={360}
          className={css({ width: 'full', objectFit: 'cover' })}
        />
      </div>

      <div className={css({ spaceY: '10', px: '5' })}>
        {viewer.__typename === 'YahooMember' ? (
          <div
            className={css({
              display: 'flex',
              alignItems: 'center',
              columnGap: '2',
            })}
          >
            {viewer.isPremium && (
              <Image
                src={`${DESIGN_SYSTEM_ASSETS_ORIGIN}/design-system/assets/brand-icons/svg/lyp.svg`}
                alt=""
                width={16}
                height={16}
              />
            )}
            <p className={css({ fontSize: 'lg' })}>
              <span className={css({ fontSize: '3xl', fontWeight: 'bold' })}>
                {viewer.name || '未設定'}
              </span>
              さん
            </p>
          </div>
        ) : (
          <div className={classes.login}>
            <a href={getLoginUrl(env, redirectBackUrl)}>ログイン</a>
            <a href={getMemberRegistrationUrl(env, redirectBackUrl)}>ID新規取得</a>
          </div>
        )}
        <div className={vstack({ gap: '6', alignItems: 'start' })}>
          <ul className={classes.menuList}>
            <ListMenu href={getReservationConfirmUrl(env)}>
              <ListOutlineIcon width={20} height={20} className={classes.icon} />
              <span>予約確認・変更</span>
            </ListMenu>
            <ListMenu href={ME_FAVORITES_PATH}>
              <HeartOutlineIcon width={20} height={20} className={classes.icon} />
              <span>お気に入り</span>
            </ListMenu>
          </ul>

          <ul className={classes.menuList}>
            <li>
              <a
                href={getHelpUrl(env)}
                className={classes.menuLink}
                target="_blank"
                rel="noreferrer"
              >
                ヘルプ・お問い合わせ
              </a>
            </li>
          </ul>
        </div>
        <ul className={classes.linkList}>
          {viewer.__typename === 'YahooMember' ? (
            <li>
              <a href={getMyPageUrl(env, redirectBackUrl)} className={classes.menuLink}>
                Yahoo! JAPAN ID登録情報
              </a>
            </li>
          ) : (
            <li>
              <a href="https://yahoo.co.jp/" className={classes.menuLink}>
                Yahoo! JAPANトップへ
              </a>
            </li>
          )}
        </ul>
      </div>
    </>
  )
}

export default SideMenu
